/**
 * Invoice Management — AEMO settlement-invoice (RCTI) tracker.
 *
 * Lives in the Contracts & Offtake section (its own Invoices page) and is also
 * embedded as a tab in the All Documents section. Same extract → process →
 * store cycle as the rest of the document pipeline: upload a weekly AEMO
 * settlement PDF, the backend parses the page-1 summary + the total generation
 * from the detailed transactions and files it against its settlement week.
 *
 * Standalone (embedded=false): visualisations on top — KPI tiles, a coverage
 * strip and per-week charts — then the period table. Missing weeks inside the
 * covered range are highlighted (and listed) so gaps are obvious.
 *
 * Embedded (embedded=true, used inside All Documents): a lean upload + table
 * panel, mirroring the FairValueWorkbooks / FinancialModels panel contract
 * {portfolioId, embedded, onCountChange}.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Alert, Chip, CircularProgress, IconButton,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axios from 'axios';
import { useThemeColors } from '../theme/ThemeModeContext';
import KpiCard, { fmtAUDCompact, fmtMWhCompact } from './KpiCard';

const API = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const GREEN = '#1f9d57';
const RED = '#e5384f';
const AMBER = '#D98A2B';

// Money with cents + sign (invoice totals are small, so don't k/M them).
const fmtMoney = (v) => {
  if (v == null || isNaN(v)) return '—';
  const s = v < 0 ? '−' : '';
  return `${s}A$${Math.abs(v).toLocaleString('en-AU', {
    minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtMWh = (v) => (v == null || isNaN(v) ? '—'
  : `${Number(v).toLocaleString('en-AU', { maximumFractionDigits: 1 })}`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-AU', {
  day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fmtDateShort = (iso) => (iso ? new Date(iso).toLocaleDateString('en-AU', {
  day: '2-digit', month: 'short' }) : '—');

const headerCellSx = {
  fontWeight: 700, fontSize: '0.7rem', color: 'text.secondary',
  textTransform: 'uppercase', letterSpacing: '0.06em',
};

const moneyColor = (v) => (v > 0 ? GREEN : v < 0 ? RED : 'inherit');

export default function InvoiceManagement({ portfolioId: pidProp,
                                            embedded = false, onCountChange }) {
  const params = useParams();
  const portfolioId = pidProp || params.portfolioId;
  const colors = useThemeColors();

  const [invoices, setInvoices] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewingId, setViewingId] = useState(null);

  const fetchList = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/portfolios/${portfolioId}/invoices`);
      const list = r.data?.invoices || [];
      setInvoices(list);
      setCoverage(r.data?.coverage || null);
      onCountChange?.(list.length);
    } catch {
      onCountChange?.(0);
    } finally {
      setLoading(false);
    }
  }, [portfolioId, onCountChange]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setMsg(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await axios.post(
        `${API}/api/portfolios/${portfolioId}/invoices/upload`,
        form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const iv = r.data?.invoice || {};
      const wk = iv.period_no ? `Week ${iv.period_no} ${iv.period_year || ''}`.trim()
                              : 'invoice';
      setMsg({
        severity: 'success',
        text: r.data?.replaced
          ? `Replaced ${wk} with the new statement.`
          : `Filed ${wk} — net ${fmtMoney(iv.statement_total)}, ${fmtMWh(iv.generation_mwh)} MWh generated.`,
      });
      fetchList();
    } catch (err) {
      setMsg({ severity: 'error',
               text: err.response?.data?.error || err.message });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/api/portfolios/${portfolioId}/invoices/${toDelete.id}`);
      setMsg({ severity: 'success',
               text: `Deleted ${toDelete.source_filename || 'invoice'}.` });
      setToDelete(null);
      fetchList();
    } catch (err) {
      setMsg({ severity: 'error',
               text: err.response?.data?.error || err.message });
    } finally {
      setDeleting(false);
    }
  };

  // Fetch the PDF with the auth header (axios interceptor) and open it.
  const handleView = async (iv) => {
    setViewingId(iv.id);
    try {
      const r = await axios.get(
        `${API}/api/portfolios/${portfolioId}/invoices/${iv.id}/file`,
        { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setMsg({ severity: 'error',
               text: err.response?.data?.error || 'Could not open the PDF.' });
    } finally {
      setViewingId(null);
    }
  };

  // ── derived: KPI roll-ups + combined (present + missing) period rows ────────
  const stats = useMemo(() => {
    const netTotal = invoices.reduce(
      (s, iv) => s + (Number(iv.statement_total) || 0), 0);
    const genTotal = invoices.reduce(
      (s, iv) => s + (Number(iv.generation_mwh) || 0), 0);
    const years = coverage?.by_year || [];
    const rangeLabel = years.length
      ? years.map((y) => `W${y.min}–W${y.max} ${y.year}`).join(', ')
      : '—';
    return {
      count: invoices.length,
      netTotal,
      genTotal,
      missing: coverage?.missing_count || 0,
      rangeLabel,
    };
  }, [invoices, coverage]);

  // Present rows + ghost (missing) rows, newest period first.
  const rows = useMemo(() => {
    const present = invoices.map((iv) => ({ ...iv, missing: false }));
    const missing = (coverage?.missing_periods || []).map((m) => ({
      ...m, id: `missing-${m.period_year}-${m.period_no}`, missing: true }));
    return [...present, ...missing].sort((a, b) =>
      (b.period_year - a.period_year) || (b.period_no - a.period_no));
  }, [invoices, coverage]);

  // Chart data: chronological (oldest→newest), last 24 weeks of the range.
  const chart = useMemo(() => {
    const ordered = [...rows].sort((a, b) =>
      (a.period_year - b.period_year) || (a.period_no - b.period_no)).slice(-24);
    return {
      labels: ordered.map((r) => `W${r.period_no}`),
      credit: ordered.map((r) => (!r.missing && r.statement_total > 0 ? r.statement_total : null)),
      charge: ordered.map((r) => (!r.missing && r.statement_total < 0 ? r.statement_total : null)),
      gen: ordered.map((r) => (r.missing ? null : (Number(r.generation_mwh) || 0))),
      missingFlags: ordered.map((r) => r.missing),
    };
  }, [rows]);

  const axisSx = {
    '& .MuiChartsAxis-tickLabel': { fill: colors.textDim, fontSize: 11 },
    '& .MuiChartsAxis-line': { stroke: colors.hairline },
    '& .MuiChartsAxis-tick': { stroke: colors.hairline },
  };

  // ── upload control (shared) ─────────────────────────────────────────────────
  const uploadButton = (
    <Button component="label" size="small" variant={embedded ? 'outlined' : 'contained'}
            startIcon={uploading ? <CircularProgress size={14} /> : <UploadFileIcon />}
            disabled={uploading}
            sx={embedded ? undefined : {
              bgcolor: 'primary.main', color: '#F4F2EC', fontWeight: 600,
              '&:hover': { bgcolor: 'primary.dark' } }}>
      {uploading ? 'Parsing…' : 'Upload invoice'}
      <input type="file" hidden accept=".pdf,application/pdf" onChange={handleUpload} />
    </Button>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        {!embedded && (
          <>
            <ReceiptLongIcon sx={{ fontSize: 26, color: 'primary.main' }} />
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
              Invoices
            </Typography>
          </>
        )}
        {embedded && (
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            AEMO Settlement Invoices
          </Typography>
        )}
        <Chip size="small" label={`${invoices.length} invoice${invoices.length !== 1 ? 's' : ''}`}
              sx={{ bgcolor: 'background.default', color: 'text.secondary',
                    border: '1px solid', borderColor: 'divider',
                    fontFamily: "'JetBrains Mono', monospace", fontSize: '0.72rem' }} />
        <Box sx={{ flexGrow: 1 }} />
        {/* Embedded in All Documents, the section's single "Upload Document"
            button handles invoice PDFs (auto-detected) — so no separate
            upload control here. The standalone Invoices page keeps its own. */}
        {!embedded && uploadButton}
      </Box>

      {!embedded && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          AEMO Recipient Created Tax Invoices (weekly NEM settlement). Each
          upload is parsed for its summary breakdown and total generation, then
          filed against its settlement week. Missing weeks in the covered range
          are highlighted below.
        </Typography>
      )}

      {msg && (
        <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>
          {msg.text}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress size={24} />
        </Box>
      ) : invoices.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center' }}>
          <ReceiptLongIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.3, mb: 1 }} />
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
            No invoices uploaded yet.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, opacity: 0.8 }}>
            {embedded
              ? 'Use “Upload Document” above to add an AEMO settlement PDF — it’s detected automatically.'
              : 'Upload a weekly AEMO settlement PDF to start tracking.'}
          </Typography>
        </Paper>
      ) : (
        <>
          {/* ── Visualisations (standalone only) ──────────────────────── */}
          {!embedded && (
            <>
              {/* KPI tiles */}
              <Box sx={{ display: 'grid', gap: 2, mb: 2,
                         gridTemplateColumns: {
                           xs: '1fr 1fr', sm: 'repeat(3, 1fr)',
                           md: 'repeat(5, 1fr)' } }}>
                <KpiCard label="Invoices" value={stats.count}
                         unit="filed" accent={AMBER} />
                <KpiCard label="Periods covered" value={stats.rangeLabel}
                         tooltip="Settlement-week range with at least one invoice on file" />
                <KpiCard label="Missing weeks" value={stats.missing}
                         accent={stats.missing > 0 ? RED : GREEN}
                         tooltip="Gaps inside the covered range" />
                <KpiCard label="Net settled" value={fmtAUDCompact(stats.netTotal)}
                         tooltip="Sum of statement totals across all filed invoices" />
                <KpiCard label="Total generation" value={fmtMWhCompact(stats.genTotal)} />
              </Box>

              {/* Missing-period banner */}
              {stats.missing > 0 && (
                <Alert severity="warning" icon={<WarningAmberIcon fontSize="small" />}
                       sx={{ mb: 2 }}>
                  {stats.missing} settlement week{stats.missing !== 1 ? 's' : ''} missing
                  in the covered range:{' '}
                  {(coverage?.missing_periods || [])
                    .map((m) => `W${m.period_no} ${m.period_year}`).join(', ')}.
                </Alert>
              )}

              {/* Coverage strip — one cell per week, gaps in red */}
              <CoverageStrip coverage={coverage} rows={rows} />

              {/* Charts */}
              <Box sx={{ display: 'grid', gap: 2, mb: 3,
                         gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Net settlement by week
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Credit (paid to you) vs charge, A$ per statement
                  </Typography>
                  <BarChart
                    height={240}
                    xAxis={[{ scaleType: 'band', data: chart.labels }]}
                    series={[
                      { data: chart.credit, label: 'Credit', stack: 'net', color: GREEN },
                      { data: chart.charge, label: 'Charge', stack: 'net', color: RED },
                    ]}
                    margin={{ top: 16, right: 8, bottom: 24, left: 56 }}
                    slotProps={{ legend: { hidden: true } }}
                    sx={axisSx}
                  />
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Generation by week
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    MWh exported to the grid (from detailed transactions)
                  </Typography>
                  <BarChart
                    height={240}
                    xAxis={[{ scaleType: 'band', data: chart.labels }]}
                    series={[{ data: chart.gen, label: 'Generation (MWh)', color: AMBER }]}
                    margin={{ top: 16, right: 8, bottom: 24, left: 48 }}
                    slotProps={{ legend: { hidden: true } }}
                    sx={axisSx}
                  />
                </Paper>
              </Box>
            </>
          )}

          {/* ── Period table ───────────────────────────────────────────── */}
          <TableContainer component={Paper}
                          sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'rgba(217, 138, 43, 0.08)' }}>
                  <TableCell sx={headerCellSx}>Period</TableCell>
                  <TableCell sx={headerCellSx}>Dates</TableCell>
                  <TableCell sx={headerCellSx}>Statement</TableCell>
                  <TableCell sx={headerCellSx} align="right">Energy</TableCell>
                  <TableCell sx={headerCellSx} align="right">FPP</TableCell>
                  <TableCell sx={headerCellSx} align="right">GST</TableCell>
                  <TableCell sx={headerCellSx} align="right">Net total</TableCell>
                  <TableCell sx={headerCellSx} align="right">Gen (MWh)</TableCell>
                  <TableCell sx={headerCellSx} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  r.missing ? (
                    <TableRow key={r.id}
                              sx={{ bgcolor: 'rgba(229, 56, 79, 0.06)',
                                    '& td': { borderBottom: '1px dashed',
                                              borderBottomColor: 'rgba(229,56,79,0.3)' } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <WarningAmberIcon sx={{ fontSize: 16, color: RED }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Week {r.period_no}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.period_year}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {r.period_start
                            ? `${fmtDateShort(r.period_start)} – ${fmtDateShort(r.period_end)}`
                            : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell colSpan={5}>
                        <Chip size="small" label="Missing — not uploaded" sx={{
                          height: 20, bgcolor: 'rgba(229,56,79,0.12)', color: RED,
                          fontWeight: 600, fontSize: '0.68rem' }} />
                      </TableCell>
                      <TableCell align="right" />
                      <TableCell align="right" />
                    </TableRow>
                  ) : (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Week {r.period_no}
                          <Typography component="span" variant="caption"
                                      color="text.secondary" sx={{ ml: 0.75 }}>
                            {r.period_year}
                          </Typography>
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {fmtDateShort(r.period_start)} – {fmtDateShort(r.period_end)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={`Due ${fmtDate(r.due_date)}`} arrow>
                          <Typography variant="caption" color="text.secondary">
                            {fmtDate(r.statement_date)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ color: moneyColor(r.summary_json?.energy),
                                                     fontVariantNumeric: 'tabular-nums' }}>
                        {fmtMoney(r.summary_json?.energy)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums',
                                                     color: 'text.secondary' }}>
                        {fmtMoney(r.summary_json?.frequency_performance_payment)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums',
                                                     color: 'text.secondary' }}>
                        {fmtMoney(r.summary_json?.gst)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700,
                                                     color: moneyColor(r.statement_total),
                                                     fontVariantNumeric: 'tabular-nums' }}>
                        {fmtMoney(r.statement_total)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {fmtMWh(r.generation_mwh)}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View PDF">
                          <span>
                            <IconButton size="small" onClick={() => handleView(r)}
                                        disabled={viewingId === r.id}
                                        sx={{ color: 'text.secondary',
                                              '&:hover': { color: 'primary.main' } }}>
                              {viewingId === r.id
                                ? <CircularProgress size={15} />
                                : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => setToDelete(r)}
                                      sx={{ color: 'text.secondary',
                                            '&:hover': { color: 'error.main' } }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Delete confirmation */}
      <Dialog open={!!toDelete} onClose={() => !deleting && setToDelete(null)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete invoice</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.secondary' }}>
            Delete the invoice for{' '}
            <b>Week {toDelete?.period_no} {toDelete?.period_year}</b>
            {' '}({toDelete?.source_filename})? This removes the stored PDF and
            its parsed summary, and the week will show as missing. This cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained"
                  disabled={deleting}
                  startIcon={deleting ? <CircularProgress size={14} /> : null}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Coverage strip: one cell per week across the covered range ─────────────────
function CoverageStrip({ coverage, rows }) {
  const byKey = useMemo(() => {
    const m = {};
    rows.forEach((r) => { if (!r.missing) m[`${r.period_year}-${r.period_no}`] = r; });
    return m;
  }, [rows]);

  const years = coverage?.by_year || [];
  if (!years.length) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
        Coverage
      </Typography>
      {years.map((y) => {
        const weeks = [];
        for (let w = y.min; w <= y.max; w += 1) weeks.push(w);
        return (
          <Box key={y.year} sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {weeks.map((w) => {
                const present = !!byKey[`${y.year}-${w}`];
                return (
                  <Tooltip key={w} arrow
                           title={present
                             ? `Week ${w} ${y.year} — filed`
                             : `Week ${w} ${y.year} — missing`}>
                    <Box sx={{
                      minWidth: 30, px: 0.5, py: 0.4, borderRadius: '4px',
                      textAlign: 'center', fontSize: '0.68rem', fontWeight: 600,
                      fontFamily: "'JetBrains Mono', monospace",
                      cursor: 'default',
                      color: present ? GREEN : RED,
                      bgcolor: present ? 'rgba(31,157,87,0.12)' : 'rgba(229,56,79,0.10)',
                      border: '1px solid',
                      borderColor: present ? 'rgba(31,157,87,0.3)' : 'rgba(229,56,79,0.35)',
                      borderStyle: present ? 'solid' : 'dashed',
                    }}>
                      {w}
                    </Box>
                  </Tooltip>
                );
              })}
              <Typography variant="caption" color="text.secondary"
                          sx={{ alignSelf: 'center', ml: 0.5 }}>
                {y.year}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
